import React, { useEffect, useMemo, useState } from 'react';
import { Skeleton, Switch } from 'antd';
import { useQuery } from '@apollo/client';
import CustomParcelsTimeline, { ExternalComparePayload } from './CustomParcelsTimeline';
import ExternalParcelsTimeline from './ExternalParcelsTimeline';
import type { ExternalOrderEventSummaryType } from '../graphql/types/OrderDetailType';
import {
    GET_INTERNAL_ORDER_TRACKING_INFO,
    GET_INTERNAL_ORDER_TRACKING_INFO_FOR_HISTORY,
    GetInternalOrderTrackingInfoForHistoryVariables,
    GetInternalOrderTrackingInfoResponse,
    GetInternalOrderTrackingInfoVariables,
    mapInternalTrackingToExternalEvents,
} from '../graphql/queries/getInternalOrderTrackingInfo';

export type ParcelHistoryDetailLike = {
    orderWithAddresses?: {
        order?: {
            trackingNumber?: string | null;
        } | null;
    } | null;
    orderEvents?: Array<{ eventId?: string }> | null;
    externalOrderEvents?: ExternalOrderEventSummaryType[] | null;
};

type ParcelHistorySectionProps = {
    parcel: ParcelHistoryDetailLike | undefined;
    channelName: string;
    showExternalTrack: boolean;
    t: (key: string, defaultValue?: string) => string;
    /** 历史包裹（已归档）详情传订单 ID：外部轨迹改查归档轨迹，不传则按运单号查在途 */
    historyOrderId?: string;
};

/**
 * 包裹历史：支持仅内部/仅外部切换；在途详情可开启「轨迹对比」在同一时间线主节点下展示对外轨迹
 * （sourceOrderEventId 与 eventId 对齐）。历史包裹详情（传入 historyOrderId）不展示轨迹对比。
 */
const ParcelHistorySection: React.FC<ParcelHistorySectionProps> = ({
    parcel,
    channelName,
    showExternalTrack,
    t,
    historyOrderId,
}) => {
    const [activeTimeline, setActiveTimeline] = useState<'internal' | 'external'>('internal');
    const [compareMode, setCompareMode] = useState(false);

    const trackingNo = parcel?.orderWithAddresses?.order?.trackingNumber?.trim() || '';
    const isHistory = historyOrderId !== undefined;
    const historyOrderIdNum = isHistory ? Number(historyOrderId) : NaN;
    const liveTracking = useQuery<GetInternalOrderTrackingInfoResponse, GetInternalOrderTrackingInfoVariables>(
        GET_INTERNAL_ORDER_TRACKING_INFO,
        {
            variables: { input: { trackingNo } },
            skip: isHistory || !showExternalTrack || !trackingNo,
            fetchPolicy: 'network-only',
        }
    );
    // 已归档订单的轨迹已搬到归档表，按运单号查在途查不到，需按订单 ID 查归档轨迹
    const historyTracking = useQuery<GetInternalOrderTrackingInfoResponse, GetInternalOrderTrackingInfoForHistoryVariables>(
        GET_INTERNAL_ORDER_TRACKING_INFO_FOR_HISTORY,
        {
            variables: { orderId: historyOrderIdNum },
            skip: !isHistory || !showExternalTrack || Number.isNaN(historyOrderIdNum),
            fetchPolicy: 'network-only',
        }
    );
    const { data: officialTrackingData, loading: officialTrackingLoading } = isHistory
        ? historyTracking
        : liveTracking;
    const officialExternalEvents = useMemo(
        () =>
            mapInternalTrackingToExternalEvents(
                officialTrackingData?.getInternalOrderTrackingInfo?.trackingEventList
            ),
        [officialTrackingData]
    );
    // 轨迹对比仍使用详情接口内嵌数据，保持现有对齐逻辑不变。
    const compareExternalEvents = parcel?.externalOrderEvents ?? [];
    const hasOfficialExternalEvents = officialExternalEvents.length > 0;
    const hasCompareExternalEvents = compareExternalEvents.length > 0;
    const hasExternalEvents = hasOfficialExternalEvents || hasCompareExternalEvents;
    const showTab = showExternalTrack && hasExternalEvents;

    useEffect(() => {
        if (!showTab) {
            setActiveTimeline('internal');
            setCompareMode(false);
        }
    }, [showTab]);

    // 历史包裹详情不提供「轨迹对比」（只保留内/外轨迹切换）
    useEffect(() => {
        if (isHistory) {
            setCompareMode(false);
        }
    }, [isHistory]);

    const externalCompare: ExternalComparePayload | undefined = useMemo(() => {
        if (isHistory || !compareMode || !compareExternalEvents.length) {
            return undefined;
        }
        const byEventId = new Map<string, ExternalOrderEventSummaryType[]>();
        const unmapped: ExternalOrderEventSummaryType[] = [];
        for (const ext of compareExternalEvents) {
            const sid = ext.sourceOrderEventId;
            if (sid == null || sid === '') {
                unmapped.push(ext);
                continue;
            }
            const k = String(sid);
            const cur = byEventId.get(k);
            if (cur) {
                cur.push(ext);
            } else {
                byEventId.set(k, [ext]);
            }
        }
        return { byEventId, unmapped };
    }, [compareExternalEvents, compareMode, isHistory]);

    return (
        <>
            <div className="mb-4 flex items-center justify-between gap-4">
                <h3 className="text-lg font-medium">{t('central.parcels.pageDetails.parcelHistory')}</h3>
            </div>
            {showTab && (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <span className="text-gray-800 text-sm font-medium">
                        {compareMode
                            ? t('central.parcels.timeline.trackingCompare', '轨迹对比')
                            : activeTimeline === 'internal'
                            ? t('central.parcels.timeline.internal', '内部轨迹')
                            : t('central.parcels.timeline.external', '外部轨迹')}
                    </span>
                    <div className="flex flex-wrap items-center gap-4 shrink-0">
                        {!isHistory && (
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500 text-xs whitespace-nowrap">
                                    {t('central.parcels.timeline.trackingCompare', '轨迹对比')}
                                </span>
                                <Switch
                                    size="small"
                                    checked={compareMode}
                                    onChange={setCompareMode}
                                    aria-label={t('central.parcels.timeline.trackingCompare', '轨迹对比')}
                                />
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-xs whitespace-nowrap hidden sm:inline">
                                {activeTimeline === 'internal'
                                    ? t('central.parcels.timeline.toggleToExternalHint', '查看外部轨迹')
                                    : t('central.parcels.timeline.toggleToInternalHint', '查看内部轨迹')}
                            </span>
                            <Switch
                                size="small"
                                disabled={!isHistory && compareMode}
                                checked={activeTimeline === 'external'}
                                onChange={(checked) => setActiveTimeline(checked ? 'external' : 'internal')}
                                aria-label={
                                    activeTimeline === 'internal'
                                        ? t('central.parcels.timeline.toggleToExternalHint', '查看外部轨迹')
                                        : t('central.parcels.timeline.toggleToInternalHint', '查看内部轨迹')
                                }
                            />
                        </div>
                    </div>
                </div>
            )}
            {parcel ? (
                !isHistory && compareMode && hasCompareExternalEvents ? (
                    <CustomParcelsTimeline
                        items={parcel.orderEvents || []}
                        channelName={channelName}
                        externalCompare={externalCompare}
                    />
                ) : activeTimeline === 'external' && hasOfficialExternalEvents ? (
                    officialTrackingLoading ? (
                        <div className="flex justify-center items-center h-32">
                            <Skeleton active paragraph={{ rows: 3 }} />
                        </div>
                    ) : (
                        <ExternalParcelsTimeline items={officialExternalEvents} />
                    )
                ) : (
                    <CustomParcelsTimeline items={parcel.orderEvents || []} channelName={channelName} />
                )
            ) : (
                <div className="flex justify-center items-center h-32">
                    <Skeleton active paragraph={{ rows: 4 }} />
                </div>
            )}
        </>
    );
};


export default ParcelHistorySection;
